import { useAuth, useUser } from "@clerk/react";
import { usePostHog } from "@posthog/react";
import { PartyPopper, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useConsumerReferral } from "../../hooks/useConsumerReferral";
import { useDashboardShellCapabilities } from "../../hooks/useDashboardShellCapabilities";
import {
  putReferralPublishPromptAck,
  type ReferralPublishPromptAck,
} from "../../lib/app-state-api";
import type { ReferralIdentity } from "../../lib/dashboard-settings";
import {
  captureReferralSurfaceClicked,
  captureReferralSurfaceDismissed,
  captureReferralSurfaceShown,
  type ReferralCapture,
} from "../../lib/referral-analytics";
import { referralSurfaceCopy } from "../../lib/referral-surface-copy";
import { ReferralShareActions } from "./referral-share-actions";

export interface ReferralPublishPromptContainerProps {
  /**
   * The durable one-time marker decoded from the `/api/users/me` bootstrap
   * (`consumer.dashboard/referral_publish_prompt.v1`). `"shown"` = the prompt
   * has fired before — never again, on any device. `null` = never shown.
   */
  ack: ReferralPublishPromptAck | null;
  /** True once a publish succeeded IN THIS SESSION (never derived from `published_at`). */
  justPublished: boolean;
  /**
   * The dashboard snapshot's own `portal_discoverable` — the SAME signal the
   * publish block below renders "Live to agents" from. The publish PUT
   * resolves before that snapshot refetch lands, so gating on `justPublished`
   * alone showed "YOU'RE LIVE" above a block still offering "Go live"
   * (caught in a PR screenshot). Defaults true for callers without a snapshot.
   */
  published?: boolean;
  /** Target-area label when the buyer is queued for a pre-launch metro, else null. */
  waitlistArea: string | null;
  /**
   * Fired once, when the prompt first becomes visible. DashboardPage
   * suppresses the passport's referral banner from this event until
   * `onDismissed` — deriving it from the click that published raced the
   * first bootstrap (the Go-live button renders from the cached snapshot),
   * so the page must follow what the prompt actually did, not predict it.
   */
  onShown?: () => void;
  /** Fired on dismiss — hands the passport back to the banner. */
  onDismissed?: () => void;
}

/**
 * One-time post-first-publish share prompt (inline card at the top of the
 * passport, deliberately not a modal — the publish confirm already stacks a
 * dialog). The highest-intent referral moment we have.
 *
 * "Exactly once, ever" is enforced by writing the `shown` marker the FIRST
 * TIME IT RENDERS (not on dismiss) — so navigating away, re-publishing, or
 * reloading can never re-show it. It renders only when there is a concrete
 * promise (`referralSurfaceCopy`), and it waits for the share link to settle
 * before deciding, so it never burns the one-time marker on a render that
 * had nothing to say.
 */
export function ReferralPublishPromptContainer({
  ack,
  justPublished,
  waitlistArea,
  onShown,
  onDismissed,
  published = true,
}: ReferralPublishPromptContainerProps) {
  const { getToken } = useAuth();
  const { user } = useUser();
  const posthog = usePostHog();
  const referral = useConsumerReferral(getToken);
  const { marketplaceEnabled } = useDashboardShellCapabilities();
  const [dismissedNow, setDismissedNow] = useState(false);
  const markedRef = useRef(false);
  const tokenRef = useRef(getToken);
  useEffect(() => {
    tokenRef.current = getToken;
  }, [getToken]);

  const identity = useMemo<ReferralIdentity>(
    () => ({
      firstName: user?.firstName ?? null,
      lastName: user?.lastName ?? null,
      email: user?.primaryEmailAddress?.emailAddress ?? null,
    }),
    [user?.firstName, user?.lastName, user?.primaryEmailAddress?.emailAddress],
  );

  const copy = referral.isLoading
    ? null
    : referralSurfaceCopy({
        incentive: referral.incentive,
        waitlistArea,
        marketplaceLive: marketplaceEnabled,
      });
  // Once shown in THIS session, stay shown until dismissed. The publish
  // mutation's onSuccess re-bootstraps /api/users/me, which reads back the
  // `shown` marker this very render wrote and flips `ack` to "shown" — without
  // this latch the prompt vanished after one frame with its one-time marker
  // already burned (found live, 2026-09-13).
  const [shownThisSession, setShownThisSession] = useState(false);
  const armed =
    shownThisSession || (justPublished && published && ack === null);
  const visible = armed && !dismissedNow && copy !== null;

  const capture = useCallback<ReferralCapture>(
    (event, properties) => posthog?.capture?.(event, properties),
    [posthog],
  );

  const shownIncentive = visible ? copy.incentive : null;
  useEffect(() => {
    if (!shownIncentive || markedRef.current) return;
    markedRef.current = true;
    setShownThisSession(true);
    onShown?.();
    captureReferralSurfaceShown(capture, "publish_prompt", shownIncentive);
    // Best-effort durable "shown" marker. A failed write only means it may
    // show once more on another device; the in-session state already holds.
    void tokenRef.current().then((token) => {
      if (token) void putReferralPublishPromptAck(token).catch(() => {});
    });
    // onShown is deliberately not a dep: this effect fires once per mount
    // (markedRef), and a parent re-render must not re-arm it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capture, shownIncentive]);

  const handleDismiss = useCallback(() => {
    if (!copy) return;
    setDismissedNow(true);
    captureReferralSurfaceDismissed(capture, "publish_prompt", copy.incentive);
    onDismissed?.();
  }, [capture, copy, onDismissed]);

  if (!visible) return null;

  return (
    <section
      data-testid="referral-publish-prompt"
      data-incentive={copy.incentive}
      aria-label="You're live — refer a friend"
      className="surface-panel relative mb-5 border border-primary/20 bg-primary/5 p-4 sm:p-5"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        data-testid="referral-publish-prompt-dismiss"
        className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <span className="rounded-xl bg-primary/10 p-2 text-primary">
          <PartyPopper className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 space-y-1">
          <p className="eyebrow-label text-primary">You&apos;re live</p>
          <h2 className="text-base font-semibold text-foreground">
            {copy.headline}
          </h2>
          <p className="text-sm leading-6 text-muted-foreground">{copy.body}</p>
        </div>
      </div>
      <ReferralShareActions
        referral={referral}
        identity={identity}
        onAction={(action) =>
          captureReferralSurfaceClicked(
            capture,
            "publish_prompt",
            copy.incentive,
            action,
          )
        }
        className="mt-4"
      />
    </section>
  );
}
